import { Form } from 'antd';
import { FC, memo, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppPagination } from '@/common/hooks';
import { FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  SearchConsoleAccountOTPFields,
  SearchConsoleAccountOTPInputs,
  SearchConsoleAccountsQueryKeys,
  useAddSearchConsoleAccountOTPMutation
} from '@/modules/DomainsApi';

import { SearchConsoleAccountOTPFormView } from './SearchConsoleAccountOTPFormView';
import { searchConsoleAccountOTPInputsValidationSchema } from './validation';

interface Props {
  searchConsoleAccountId: string;
  handleCloseCreateMode: () => void;
}

export const SearchConsoleAccountOTPFormContainer: FC<Props> = memo(({
  searchConsoleAccountId,
  handleCloseCreateMode
}) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<SearchConsoleAccountOTPFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof SearchConsoleAccountOTPInputs>>(null);
  const paginationOptions = useAppPagination();

  const { mutate: addOTPMutate, isLoading } = useAddSearchConsoleAccountOTPMutation(searchConsoleAccountId)({
    onSuccess: async (data) => {
      handleCloseCreateMode();

      appMessageApi.success('OTP токен успішно додано!');

      queryClient.setQueryData([SearchConsoleAccountsQueryKeys.GetOneById, searchConsoleAccountId], (dt?: object) => {
        return {
          ...dt,
          ...data
        };
      });

      await queryClient.refetchQueries([SearchConsoleAccountsQueryKeys.GetAll, paginationOptions], { exact: true });
    },
    onError: (error) => {
      setFormErrors(extractFormErrors<typeof SearchConsoleAccountOTPInputs>(error));
      messageOnError(error, appMessageApi);
    },
  });

  const initialValues: SearchConsoleAccountOTPFields = {
    secret: undefined,
  };

  const handleFinish = (values: SearchConsoleAccountOTPFields) => {
    addOTPMutate(values);
  };

  const handleFinishFailed = (): void => {
    setFormErrors(null);
  };

  const handleBack = () => {
    handleCloseCreateMode();
  };

  return (
    <SearchConsoleAccountOTPFormView
      form={ form }
      initialValues={ initialValues }
      formErrors={ formErrors }
      formName="search-console-account-otp-add"
      formType="create"
      isFormLoading={ isLoading }
      isButtonsDisabled={ isLoading }
      validationSchema={ searchConsoleAccountOTPInputsValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
